import type {DesignerStores} from '@packages/systems/designer/flux';
import {AiCreditBillingState, SiteDataStateType} from './SiteDataStateType';
import {
  SiteRolesType,
  WORKSPACE_ROLES,
} from '@packages/domains/authorization/permissions/catalog';
import {SiteKind} from '@packages/domains/site-access/data-types';
import {emptyArray, emptyObject} from '@packages/utilities/fp/utils';
import {getNumericSiteVersion} from '@packages/systems/site-data-migrations';

export type {
  AiCreditBillingState,
  RemoteState,
  SiteDataStateType,
  Template,
  RemoteCustomFont,
  WebhookTriggerType,
  WebhookResponse,
  WebhookFilter,
} from './SiteDataStateType';

export const selectSiteId: (arg1: DesignerStores) => string = (state) =>
  state.SiteDataStore.siteId;

export const selectSiteCreatedOn: (
  arg1: DesignerStores
) => string | Date | null = (state) => state.SiteDataStore.createdOn;

export const selectUserCreatedOn: (arg1: DesignerStores) => string | null = (
  state
) => state.SiteDataStore.userCreatedOn;

export const getEmailFormTarget = (state: DesignerStores): string =>
  state.SiteDataStore.emailFormTarget;

export const siteDataSelector = (state: DesignerStores): SiteDataStateType =>
  state.SiteDataStore;

// Safe since `getNumericSiteVersion` returns a primitive number:
// eslint-disable-next-line webflow/selector-memoization
export const selectSiteDataVersion = (state: DesignerStores): number =>
  getNumericSiteVersion(state.SiteDataStore?.version ?? undefined);

/**
 * Selects the workspace plan from the SiteDataStore
 */
export const selectWorkspacePlan = (state: DesignerStores): string | null =>
  state.SiteDataStore.workspacePlan ?? null;

export const selectWorkspaceId: (state: DesignerStores) => string | null = (
  state
) => state.SiteDataStore.workspaceId;

export const selectWorkspaceSlug: (state: DesignerStores) => string | null = (
  state
) => state.SiteDataStore.workspaceSlug;

/**
 * Selects the AI-credit billing resolution for the current user, resolved
 * server-side during Designer boot (guests only; `null` for non-guests).
 */
export const selectAiCreditBilling = (
  state: DesignerStores
): AiCreditBillingState | null => state.SiteDataStore.aiCreditBilling ?? null;

/**
 * The workspace whose AI-credit balance the Designer should display and link
 * to — the agency (billing) workspace for redirected agency guests, otherwise
 * the host. Mirrors where credit usage is actually billed (the resolver), so
 * the displayed balance matches the charged workspace. Falls back to the host
 * for non-guests / flag-off / non-redirected.
 */
export const selectAiCreditBillingWorkspaceId = (
  state: DesignerStores
): string | null => {
  const billing = state.SiteDataStore.aiCreditBilling;
  return billing?.status === 'redirected' && billing.billingWorkspaceId
    ? billing.billingWorkspaceId
    : selectWorkspaceId(state);
};

/** Slug counterpart of {@link selectAiCreditBillingWorkspaceId}, for usage/credit-pack links. */
export const selectAiCreditBillingWorkspaceSlug = (
  state: DesignerStores
): string | null => {
  const billing = state.SiteDataStore.aiCreditBilling;
  return billing?.status === 'redirected' && billing.billingWorkspaceSlug
    ? billing.billingWorkspaceSlug
    : selectWorkspaceSlug(state);
};

/**
 * Display name of the billing workspace when AI-credit usage should show a
 * guest attribution line ("Credits billed to {workspace}"):
 * - redirected agency guests → agency workspace name
 * - guests billing the host (opt-in or kill-switch off) → host workspace name
 * - non-guests → null (no attribution line)
 *
 * Guest vs non-guest is encoded in aiCreditBilling itself: the Designer only
 * loads a billing payload for workspace guests, so status 'host' / 'redirected'
 * is already guest-scoped.
 */
export const selectAiCreditBillingWorkspaceName = (
  state: DesignerStores
): string | null => {
  const billing = state.SiteDataStore.aiCreditBilling;

  if (billing?.status === 'redirected' && billing.billingWorkspaceName) {
    return billing.billingWorkspaceName;
  }

  if (billing?.status === 'host') {
    return state.SiteDataStore.workspaceName ?? null;
  }

  return null;
};

export const selectWorkspaceName = (state: DesignerStores): string | null =>
  state.SiteDataStore.workspaceName ?? null;

/**
 * Display name of the workspace that AI credits are billed to — the agency
 * workspace for redirected guests, otherwise the host workspace. Use this
 * (rather than selectAiCreditBillingWorkspaceName) when you need a name for
 * all users, not just redirected guests.
 */
export const selectAiCreditBillingWorkspaceDisplayName = (
  state: DesignerStores
): string | null => {
  const billing = state.SiteDataStore.aiCreditBilling;
  return billing?.status === 'redirected' && billing.billingWorkspaceName
    ? billing.billingWorkspaceName
    : selectWorkspaceName(state);
};

/**
 * Whether the current user is a legacy guest (a guest with no agency
 * workspace to bill) and therefore blocked from AI credit usage until they
 * convert via the Agency-as-Guest flow.
 */
export const selectIsLegacyGuestAiBlocked = (state: DesignerStores): boolean =>
  state.SiteDataStore.aiCreditBilling?.status === 'blocked-legacy';

/**
 * Whether the current user's AI credit usage is redirected to their home
 * (agency) workspace — i.e. they're an agency guest whose credits bill back
 * to their own workspace rather than the host. Drives the SP-usage FTUX
 * notice (PYMT-6064).
 */
export const selectShouldRedirectCredit = (state: DesignerStores): boolean =>
  state.SiteDataStore.aiCreditBilling?.status === 'redirected';

/**
 * ISO date the current user's WorkspaceRoleMap was created (guests only; `null`
 * otherwise). Fed into the Knock guide targeting `data` for membership-age
 * conditions (e.g. the SP-usage notice for pre-cutover guests).
 */
export const selectWorkspaceRoleMapCreatedOn = (
  state: DesignerStores
): string | null => state.SiteDataStore.workspaceRoleMapCreatedOn ?? null;

export const selectSitePlan: (state: DesignerStores) => string | null = (
  state
) => state.SiteDataStore.siteSubscription?.type || null;

// Mirrors `stripPrefix` from `@packages/systems/designer/users` — duplicated
// (per the "duplicate simple utils over circular deps" rule) to avoid a package
// cycle (site-data ↔ designer/users via designer/flux). `SiteDataStore.userId`
// can carry a `Person_`/`Collaborator_`/`Flow_` prefix, but `UserStore.siteUsers`
// is keyed by the bare id, so the prefix must be stripped before the lookup.
function stripUserIdPrefix(id: string): string {
  return id
    .replace(/^Person_/, '')
    .replace(/^Collaborator_/, '')
    .replace(/^Flow_/, '');
}

export const selectCurrentUserWorkspaceRole: (
  state: DesignerStores
) => string | null = (state) => {
  const userId = state.SiteDataStore.userId;
  const currentUser = state.UserStore.siteUsers?.get(stripUserIdPrefix(userId));
  return currentUser?.roles?.workspace || null;
};

/**
 * Whether the current user can purchase AI credits for the workspace that
 * actually gets charged — the agency (billing) workspace for redirected agency
 * guests, otherwise the host. Owners/admins can complete the dashboard
 * checkout; everyone else is pointed at their admin. One gate so the
 * out-of-credits toast and the inline banner agree on who sees "Add credits".
 */
export const selectCanPurchaseAiCredits = (state: DesignerStores): boolean => {
  const billing = state.SiteDataStore.aiCreditBilling;
  const role =
    billing?.status === 'redirected'
      ? (billing.billingWorkspaceRole ?? null)
      : selectCurrentUserWorkspaceRole(state);
  return role === WORKSPACE_ROLES.owner || role === WORKSPACE_ROLES.admin;
};

/**
 * Selects the current user's site role from the SiteDataStore
 */
export const selectSiteRole = (state: DesignerStores): SiteRolesType | null =>
  state.SiteDataStore.role ?? null;

/**
 * Selects the default site role from the SiteDataStore
 */
export const selectDefaultSiteRole = (state: DesignerStores): string | null =>
  state.SiteDataStore.defaultSiteRole ?? null;

export const initialSiteDataState: SiteDataStateType = {
  siteId: '',
  pageId: '',
  userId: '',
  siteName: '',
  shortName: '',
  kind: SiteKind.enum.webflow,
  isOrg: false,
  isOrgEnterprise: false,
  orgId: '',
  orgTotalSites: null,
  orgSeats: null,
  siteProfileSlug: null,
  accessKey: '',
  s3bucket: '',
  csrfToken: null,
  createdOn: null,
  maxPages: 0,
  maxFields: 0,
  maxItems: 0,
  maxItemRefs: 0,
  maxCollections: 0,
  maxEcomItems: 0,
  maxLocales: 0,
  previewMode: false,
  preferredUrl: '',
  allowCustomCode: false,
  allowFormFileUpload: false,
  allowDocumentUploads: false,
  allowEcommerce: false,
  allowExport: false,
  allowPasswordProtection: false,
  allowPublishDomain: null,
  allowPublish: false,
  allowAdvancedPublish: false,
  allowScheduledSIP: false,
  allowSiteSearch: false,
  searchIndexPeriodAuto: 0,
  searchIndexPeriodManual: 0,
  seoTitle: null,
  seoDesc: null,
  database: {
    _id: '',
  },
  protected: false,
  typeKitFonts: emptyArray,
  typeKitId: null,
  googleMapsApiKey: null,
  recaptchaRequired: false,
  recaptchaSiteKey: null,
  customFonts: emptyArray,
  userEmail: '',
  userCreatedOn: null,
  siteBasePath: null,
  lastPublicationId: null,
  lastPublished: null,
  isCloneable: false,
  ecommPublishableKey: null,
  // @ts-expect-error - Error uncovered by adding type coverage for emptyObject
  siteSubscription: emptyObject,
  templateType: null,
  templateOf: null,
  visibleBackups: Infinity,
  integrations: {
    figma: {clientId: ''},
    github: {clientId: ''},
  },
  publishingOptions: {
    sslHosting: true,
    beautifyHtml: true,
    defaultSitemap: false,
    uglifyCss: false,
    minifyJs: true,
    perPageJsAsync: false,
    perPageCss: false,
    secureFrameHeaders: false,
    hstsHeader: false,
    hstsWithSubdomains: false,
    hstsWithPreload: false,
    contentConverter: false,
  },
  gsapIntegrationSettings: {
    isGsapEnabled: false,
    gsapPluginsEnabled: [],
  },
  globalComponentSettings: {
    components: [],
  },
  onboardingPersistentState: null,
  dl: null,
  version: null,
  tutorial: null,
  workspaceName: null,
  workspaceSlug: null,
  dashboardFolder: null,
  workspaceId: null,
  workspacePlan: null,
  workspaceSeatCount: null,
  workspaceCopilotEnabled: true,
  workspaceAiWriteEnabled: true,
  workspaceIsEnterprise: false,
  workspacePartnerSandboxFor: null,
  aiCreditBilling: null,
  workspaceCreatedAt: null,
  workspaceRoleMapCreatedOn: null,
  devlinkConfigs: null,
  isStagingPrivate: false,
  _APPS_encryptedSiteInfo: null,
  role: null,
  codeComponentsSafeModeReason: undefined,
  copilotEnabled: false,
  aiWriteEnabled: false,
  isAISiteGen: false,
  customCodeSiteHead: '',
  customCodeSitePostBody: '',
  themeRecommendations: [],
  theme: null,
  sections: {},
  libraryTagStylesSettings: [],
  emailFormTarget: '',
  webhooks: [],
  externalCustomCode: undefined,
  aisbWfLibCssVersion: 'v1',
  siteSpecificAccess: false,
  realtimeDisabled: false,
  isInRealtimeRollout: false,
};
